import { VisuallyHidden } from '@s-group/design-system-components/VisuallyHidden/VisuallyHidden'
import classNames from 'classnames'
import { ReactNode } from 'react'
import { styled } from 'styled-components'

import { ErrorAndDescriptionRow } from '../common/ErrorAndDescriptionRow'
import { Label } from '../dataDisplay/Label'
import { Colors } from '../global/colors'
import { InputFieldWrapper } from '../global/CommonStyling'
import { FormLabelledCheckBoxProps, setFontThemeCss, ThemedComponentProps } from '../global/ComponentProps'
import {
  BREAKPOINT_LARGE_DESKTOP_PX,
  HORIZONTAL_PADDING_DEFAULT,
  LARGE_CONTAINER_MAX_WIDTH_PX,
  VERTICAL_PADDING_DEFAULT,
} from '../global/constants'
import { SPACE_SMALL } from '../global/spacing'
import { Checkbox } from './primitive/Checkbox'

const CheckboxWrapperLabel = styled.div<ThemedComponentProps>`
  color: ${Colors.TextDefaultNeutral};
  ${(props: ThemedComponentProps) => props.$theme && setFontThemeCss(props.$theme)};

  &.disabled {
    color: ${Colors.ElementMediumNeutral};
    * {
      color: ${Colors.ElementMediumNeutral};
    }
  }
`

// long label in safari shrinks the checkbox
const CheckboxMinWidth = styled(Checkbox)`
  min-width: 1.75rem;
`

const LabelWrapper = styled.label<FormLabelledCheckBoxProps>`
  flex-grow: 1;

  .disabled {
    color: ${Colors.ElementMediumNeutral};
  }
  cursor: pointer;

  ${(props: Partial<FormLabelledCheckBoxProps>) =>
    props.$isGrouped &&
    `
    padding: ${VERTICAL_PADDING_DEFAULT}px ${HORIZONTAL_PADDING_DEFAULT}px;
  `}
`

const CUSTOM_VERTICAL_PADDING_FOR_GROUPED_CHECKBOXES = VERTICAL_PADDING_DEFAULT + 2
const CUSTOM_HORIZONTAL_PADDING_FOR_GROUPED_CHECKBOXES = HORIZONTAL_PADDING_DEFAULT + 2

const InputFieldFlexWrapper = styled(InputFieldWrapper)<FormLabelledCheckBoxProps>`
  display: flex;
  ${(props: Partial<FormLabelledCheckBoxProps>) =>
    props.$isGrouped &&
    `> label {
      padding: ${CUSTOM_VERTICAL_PADDING_FOR_GROUPED_CHECKBOXES}px ${CUSTOM_HORIZONTAL_PADDING_FOR_GROUPED_CHECKBOXES}px;
    }
  `}
  @media (min-width: ${BREAKPOINT_LARGE_DESKTOP_PX}px) {
    max-width: ${(props) => (props.$isOneColumnLayout ? `${LARGE_CONTAINER_MAX_WIDTH_PX}px` : undefined)};
  }
`

const BorderedWrapper = styled(InputFieldFlexWrapper)`
  margin-top: 3px;
  border-radius: 8px;
  border: 1px solid ${Colors.BorderWeakNeutral};
  overflow: hidden;

  > :hover:not(.disabled) {
    background-color: ${Colors.BackgroundWeakNeutral};
  }

  align-items: center;

  &.error {
    border-color: ${Colors.BorderError};
  }

  > :first-child {
    padding: ${VERTICAL_PADDING_DEFAULT}px ${HORIZONTAL_PADDING_DEFAULT}px;
    > :first-child {
      align-items: center;
    }
  }
`

const CenteredFlexContainer = styled.div<FormLabelledCheckBoxProps>`
  display: flex;
  gap: ${SPACE_SMALL};
  ${(props: Partial<FormLabelledCheckBoxProps>) =>
    props.$shouldCenterItemsVertically &&
    `
     > * {
        align-items: center;
      }
  `};
`

const RightSidedFlexContainer = styled(CenteredFlexContainer)`
  justify-content: space-between;
`

const LeftSidedFlexContainer = styled(CenteredFlexContainer)`
  > :last-child {
    padding-left: ${HORIZONTAL_PADDING_DEFAULT}px;
  }
`

export const FormLabelledCheckbox = (props: FormLabelledCheckBoxProps): ReactNode => {
  const {
    id,
    required,
    description,
    side = 'right',
    border = true,
    label,
    error,
    $shouldCenterItemsVertically = border,
    $isGrouped = false,
    isOneColumnLayout,
  } = props

  const { className, ...checkboxProps } = props

  const hasDescription = !!description
  const hasError = !!error

  const checkbox = (
    <CheckboxMinWidth
      key={`${id}-checkbox`}
      aria-describedby={hasDescription ? `${id}-desc` : undefined}
      {...checkboxProps}
    />
  )
  const FlexContainer = side === 'left' ? LeftSidedFlexContainer : RightSidedFlexContainer

  const labelComponent = (
    <CheckboxWrapperLabel key={`${id}-labelwrapper`} className={classNames({ disabled: props.disabled })}>
      {label && (
        <Label
          id={id}
          label={label}
          description={description}
          ariaHiddenDescription={true}
          isRequired={required}
          useBodyText={true}
          as="span"
        />
      )}
    </CheckboxWrapperLabel>
  )
  const components = [checkbox, labelComponent]

  const LabeledCheckbox = (
    <LabelWrapper
      id={`${id}-descriptors`}
      key={`${id}-descriptors`}
      name={`${id}-descriptors`}
      htmlFor={id}
      className={classNames({ disabled: props.disabled })}
      $isGrouped={$isGrouped}
    >
      <FlexContainer
        id={`${id}-descriptors`}
        name={`${id}-descriptors`}
        $shouldCenterItemsVertically={$shouldCenterItemsVertically}
      >
        {side === 'left' ? components : [...components].reverse()}
      </FlexContainer>
    </LabelWrapper>
  )

  const ForCheckBoxWrapper = border ? BorderedWrapper : InputFieldFlexWrapper

  return (
    <div className={className}>
      {hasError && <ErrorAndDescriptionRow id={id} error={error} />}
      <ForCheckBoxWrapper
        id={`${id}-descriptors`}
        name={`${id}-descriptors`}
        className={classNames({ error: hasError })}
        $isGrouped={$isGrouped}
        $isOneColumnLayout={isOneColumnLayout}
      >
        {LabeledCheckbox}
        {description && <VisuallyHidden id={`${id}-desc`}>{description}</VisuallyHidden>}
      </ForCheckBoxWrapper>
    </div>
  )
}
